import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { useAppState } from '../contexts/AppStateContext';
import { generateShareLink, getShareInfo } from '../utils/seoUtils';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiShare2, FiCopy, FiCheck, FiX } = FiIcons;

const SHARE_LABELS = {
  zh: { share: '分享此工具', copied: '已複製！', copy: '複製連結' },
  en: { share: 'Share This Tool', copied: 'Copied!', copy: 'Copy Link' },
  ja: { share: 'このツールをシェア', copied: 'コピー済み！', copy: 'リンクをコピー' },
  es: { share: 'Compartir Esta Herramienta', copied: '¡Copiado!', copy: 'Copiar Enlace' },
};

const PLATFORMS = [
  { id: 'twitter', label: 'X / Twitter', color: 'bg-black hover:bg-gray-800', emoji: '𝕏' },
  { id: 'facebook', label: 'Facebook', color: 'bg-blue-600 hover:bg-blue-700', emoji: 'f' },
  { id: 'linkedin', label: 'LinkedIn', color: 'bg-blue-700 hover:bg-blue-800', emoji: 'in' },
  { id: 'whatsapp', label: 'WhatsApp', color: 'bg-green-500 hover:bg-green-600', emoji: '💬' },
  { id: 'telegram', label: 'Telegram', color: 'bg-sky-500 hover:bg-sky-600', emoji: '✈️' },
];

const ShareButtons = () => {
  const { currentLang } = useLanguage();
  const { state } = useAppState();
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const labels = SHARE_LABELS[currentLang] || SHARE_LABELS.zh;
  const toolId = state.currentTool || null;
  const shareInfo = getShareInfo(currentLang, toolId);

  const handleShare = (platform) => {
    const link = generateShareLink(platform, currentLang, toolId);
    window.open(link, '_blank', 'noopener,noreferrer,width=600,height=400');
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareInfo.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = shareInfo.url;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="relative">
      <motion.button
        className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors"
        onClick={() => setIsOpen(!isOpen)}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        aria-label={labels.share}
      >
        <SafeIcon icon={FiShare2} className="w-4 h-4" />
        <span className="hidden sm:inline">{labels.share}</span>
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              className="fixed inset-0 z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
            />
            <motion.div
              className="absolute right-0 mt-2 w-52 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50"
              initial={{ opacity: 0, y: -8, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.95 }}
              transition={{ duration: 0.15 }}
            >
              <div className="p-2 space-y-1">
                {PLATFORMS.map((p) => (
                  <motion.button
                    key={p.id}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-white text-sm font-medium transition-colors ${p.color}`}
                    onClick={() => { handleShare(p.id); setIsOpen(false); }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <span className="w-5 text-center text-xs font-bold">{p.emoji}</span>
                    {p.label}
                  </motion.button>
                ))}
                <div className="border-t border-gray-100 pt-1 mt-1">
                  <motion.button
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium transition-colors"
                    onClick={handleCopy}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <SafeIcon icon={copied ? FiCheck : FiCopy} className={`w-4 h-4 ${copied ? 'text-green-600' : ''}`} />
                    {copied ? labels.copied : labels.copy}
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ShareButtons;